/**
 * Session-scope predicates shared by the auth layer and the brand filter.
 *
 * Kept as a standalone pure module (no React, no imports from the app tree) so the
 * scope rules stay reviewable and testable independently of where they're consumed.
 */

import type { AggregateSessionScope, ScopedBrand, ScopedBrandLine, SessionScope, WireSessionScope } from '@/types/auth';

/** Backends that predate aggregate sessions omit `kind`; absent means 'line'. */
export function normalizeSessionScope(raw: WireSessionScope | null | undefined): SessionScope | null {
  if (!raw) return null;
  return raw.kind === 'aggregate' ? raw : { ...raw, kind: 'line' };
}

export function isAggregateScope(scope: SessionScope | null | undefined): scope is AggregateSessionScope {
  return scope?.kind === 'aggregate';
}

/**
 * The line a brand-wide action (kill switch, "can the AI answer here?") acts on: the brand's own
 * master line, falling back to its first line for a brand that only has per-sucursal ones.
 * Mirrors the backend's `masterLineForBrand` pick rule — keep the two in step.
 */
export function masterLineOf(brand: ScopedBrand | null | undefined): ScopedBrandLine | undefined {
  if (!brand) return undefined;
  return brand.lines.find((line) => line.locationId === null) ?? brand.lines[0];
}

/**
 * Whether two session scopes grant the same thing. GET /sso/session re-derives the
 * scope on every call, so a mismatch against the scope cached at login means the
 * account was re-scoped server-side — grounds for `use-auth` to escalate on the
 * FIRST auth error instead of forgiving it as transient.
 *
 * locationName is cosmetic (a rename is not a re-scope) and excluded. For aggregate
 * sessions only the subtree root counts: the brand catalogue is re-resolved on every
 * refresh, so comparing it would log the operator out the moment a brand is added,
 * renamed or gains a line.
 */
export function sameScope(a: SessionScope | null | undefined, b: SessionScope | null | undefined): boolean {
  if (!a || !b) return !a === !b;
  if (isAggregateScope(a) || isAggregateScope(b)) {
    return isAggregateScope(a) && isAggregateScope(b) && a.rootMasterClientId === b.rootMasterClientId;
  }
  return (
    a.brandId === b.brandId &&
    a.locationId === b.locationId &&
    a.readOnly === b.readOnly &&
    a.hasOwnLine === b.hasOwnLine
  );
}
